import _get from 'lodash/get';
import _includes from 'lodash/includes';
import _isFunction from 'lodash/isFunction';
import { errorProcessor } from './errorProcessor';
import { createAnonymousToken, createTokenWithRefreshToken } from '../../actions/authUser';
import { readCookie } from '../../components/common/Cookie';
import { signout } from '../../userauth/actions/signinAction';
import { validateEmail } from '../../utils/inputValidator';
import properties from '../ajio-util/properties';
import * as ga from '../ga';
import isNode from '../ajio-util/isNode';
import { gtmEventPush } from '../ga/GTMdatalayer';

const https = require('https');

let httpsAgent = false;
if (isNode()) {
  httpsAgent = new https.Agent({
    secureProtocol: 'TLSv1_2_method'
  });
}

const timeoutNode = properties.getProperty('timeoutNode') || 30000;
const timeoutClient = properties.getProperty('timeoutClient') || 40000;
const getAgent = _parsedURL => {
  if (_parsedURL.protocol == 'http:') {
    return false;
  }
  return httpsAgent;
};
let initialTime = 0;

// Bucket -> timeout lookup. All per-upstream timeouts live on a single
// global.__TIMEOUTS__ object populated by src/initFlags/initFlags.js.
// To add a bucket: add it there + in src/initFlags/featureFlags.js.
// Canonical inventory: /tmp/ajio-timeout-buckets.md.
function getBucketTimeout(bucket) {
  return _get(global, ['__TIMEOUTS__', bucket]);
}

// Coerce 'OFF' -> 0 (disable timeout), and validate the value is a number.
// Falls back to `fallback` for any non-numeric, non-'OFF' value.
function resolveTimeout(value, fallback) {
  if (value === 'OFF') return 0;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return fallback;
}

export function fetchGetJSONReqOpts(configObj = {}) {
  const { bucket, timeout: explicitTimeout, ...rest } = configObj;

  // Pick the right timeout: explicit `timeout` wins; otherwise bucket-mapped global;
  // otherwise the legacy __TIMEOUT__ default. Client-side keeps its own default.
  let timeoutBased = isNode() ? timeoutNode : timeoutClient;
  if (isNode()) {
    let rawTimeout;
    if (explicitTimeout !== undefined) {
      rawTimeout = explicitTimeout;
    } else if (bucket) {
      rawTimeout = getBucketTimeout(bucket);
      if (rawTimeout === undefined) {
        // Unknown bucket or __TIMEOUTS__ not initialized yet (e.g. firebase fetch
        // in flight at boot); fall back to the legacy global.
        rawTimeout = _get(global, '__TIMEOUT__', timeoutNode);
      }
    } else {
      rawTimeout = _get(global, '__TIMEOUT__', timeoutNode);
    }
    timeoutBased = resolveTimeout(rawTimeout, timeoutNode);
  }

  const agent = !isNode() ? false : getAgent;
  const defaultRequestObj = {
    method: 'GET',
    headers: {
      Accept: 'application/json'
    },
    agent,
    timeout: timeoutBased,
    credentials: 'include'
  };

  let requestOptionObj = { ...defaultRequestObj };

  if (Object.keys(rest).length) {
    for (const keys in rest) {
      const obj = {};
      const newConfig = rest[keys];

      if (defaultRequestObj.hasOwnProperty(keys)) {
        const defaultObj = defaultRequestObj[keys];
        if (typeof defaultObj !== 'object') {
          obj[keys] = rest[keys];
        } else {
          obj[keys] = { ...defaultObj, ...newConfig };
        }
      } else {
        obj[keys] = rest[keys];
      }
      requestOptionObj = { ...requestOptionObj, ...obj };
    }
  }

  return requestOptionObj;
}

export function fetchGetJSONResHandler(response) {
  if (!response.ok) {
    return Promise.reject(response);
    // throw Error(response);
  }
  return response.json();
}

export function noJSONResHandler(response) {
  if (!response.ok) {
    return Promise.reject(response);
    // throw Error(response);
  }
  const { headers = {} } = response;
  const contentType = _isFunction(headers.get) ? headers.get('content-type') : undefined;
  if (_includes(contentType, 'application/json')) {
    return response.json();
  }
  return Promise.resolve(response);
}

/**
 *
 * Above function and following function(alias : this) needs to get merge into one.
 *
 * This is used in apply-voucher, reset-voucher in cart oage
 */

export function fetchJSONResHandler(response) {
  if (!response.ok) {
    return Promise.reject(response);
    // throw Error(response);
  }
  const { headers = {} } = response;
  const contentType = _isFunction(headers.get) ? headers.get('content-type') : undefined;
  if (_includes(contentType, 'application/json')) {
    return response.json();
    // } else {
    //   return Promise.resolve(response);
    // }
  }
}

export function getErrorStatusCode(err) {
  let { status, type = '' } = err;
  if (type == 'request-timeout') {
    status = 504;
  }
  return status;
}

export function errorParser(error = {}, statusCode = '') {
  gtmEventPush({ event: 'http_error', error_code: statusCode });
  return new Promise((resolve, reject) => {
    errorProcessor(error)
      .then(errorObject => {
        // if (typeof errorObject === "string") {
        //   throw Error(errorObject);
        // }
        let flatErrorObject = {
          statusCode
        };

        /**
         * Error with code = 400 is only visible to user.
         * Other type of error will be silent from user UI but will be available in redux store.
         */

        if (statusCode === 400) {
          flatErrorObject = { ...flatErrorObject, errorType: 'info', ...errorObject };
        } else {
          flatErrorObject = { ...flatErrorObject, errorType: 'basic', ...errorObject };
        }

        if (errorObject.refreshToken) {
          flatErrorObject = { ...flatErrorObject, refreshToken: errorObject.refreshToken };
        }
        /**
         * Reading (errors) object from server response object and
         * based on type we are setting in flat structure.
         */

        let { errorMessage: { errors = '' } = {} } = errorObject;

        if (Array.isArray(errors)) {
          errors.map(item => {
            let errorObj = item;
            if (typeof item !== 'object') {
              errorObj = {};
              errorObj.message = JSON.stringify(item);
              errorObj.type = 'primitiveError';
            }
            flatErrorObject = { ...flatErrorObject, ...errorObj };
          });
        } else if (typeof errors === 'object') {
          flatErrorObject = { ...flatErrorObject, ...errors };
        } else {
          const errorObj = {};
          // Remove: Something went wrong. Try again error
          errors = errors || '';
          errorObj.message = toMessage(errors);
          // errorObj.message = JSON.stringify(errors);
          errorObj.type = 'primitiveError';
          flatErrorObject = { ...flatErrorObject, ...errorObj };
        }

        /**
         *  If Error message is missing, since this parameter is visible to user.
         *  If errorType is "info".
         */

        if (!flatErrorObject.message) {
          flatErrorObject = {
            ...flatErrorObject,
            message: 'Sorry !! Currently we are unable reach our server.',
            errorType: 'info'
          };
        }
        const { statusText = '', url = '' } = error;
        const { message = '' } = flatErrorObject;
        const formattedUrlArr = url.split('/') || [];
        const { length } = formattedUrlArr;
        let formattedUrl = [];
        for (let i = 0; i < length; i++) {
          if (validateEmail(formattedUrlArr[i])) {
            formattedUrl.push(' ');
          } else {
            formattedUrl.push(formattedUrlArr[i]);
          }
        }

        formattedUrl = formattedUrl.join('/');
        if (window) {
          window.GA_Error = {
            statusCode,
            statusText,
            message,
            url: formattedUrl,
            date: new Date()
          };
        }

        /** GOOGLE ANALYTICS for Node side.
         * Send Parsed Error Message to GA for tracking Backend API errors;
         * only if we're running this in Node;
         *
         */
        /*
      if (isNode()) {

        const backEndUrl = _get(errorObject, "error.url", _get(error, "url", "UnknownUrl"));

        try {
          const eventCategory = "serviceErrorEvent_ajio";
          const eventAction = backEndUrl;
          const eventLabel = `${flatErrorObject.statusCode || "NoStatusCode"}: ${flatErrorObject.message || "NoErrorMessage"}`;
          ga.gaSendEvent(eventCategory, eventAction, eventLabel);

        } catch (error) {
          console.log("Unable to send.", error);
        }
      } */
        resolve(flatErrorObject);
      })
      .catch(errorInErrorObject => {
        /**
         * Explicitly setting for slient exit if parsing fails for error object.
         * So that the user is blocked. Need to track this kind of slient exit in GA.
         *
         */
        try {
          // Create event details
          const eventCategory = 'serviceErrorEvent_ajio';
          const eventAction = _get(error, 'url', 'UnkownUrl');
          const eventLabel = '404: Oops !! something went wrong. Try after sometime.';
          ga.gaSendEvent(eventCategory, eventAction, eventLabel);
        } catch (error) {
          console.log('Unable to send.', error);
        }
        resolve({
          statusCode: 404,
          errorType: 'info',
          type: 'FailedToParseError',
          message: 'Oops !! something went wrong. Try after sometime.'
        });
      });
  });
}

export function tokenReCreateParser(status, error = {}, request = {}) {
  let user = readCookie('U');
  if (request.cookies) {
    user = request.cookies.U;
  }
  let req = {
    ...request
  };

  if (error.refreshToken) {
    req = {
      ...req,
      refreshToken: error.refreshToken
    };
  }

  return new Promise((resolve, reject) => {
    if (status === 401 && user === 'anonymous') {
      createAnonymousToken(request)
        .then(response => {
          resolve('success');
        })
        .catch(err => reject(err));
    } else if (status === 401 && user !== 'anonymous') {
      createTokenWithRefreshToken(req)
        .then(response => {
          resolve(response);
        })
        .catch(err => {
          signout(() => {
            window.location = '/';
          })();
        });
    } else {
      reject(error);
    }
  });
}

export function loggerRequest(url) {
  initialTime = new Date().getTime();
}

export async function writeToLog(stringToLog, val1 = '', val2 = '') {
  const flag = _get(global, '__WRITE_TO_LOG__', 'OFF') === 'ON';
  if (!flag) return;
  const fs = require('fs');
  const filename = 'logs/writeToLog.log';
  const logStr = `${stringToLog} ${val1} ${val2}\n`;
  fs.appendFile(filename, logStr, function(err) {
    if (err) throw err;
  });
}

export function loginHostUrl(login_url, endpoint) {
  if (login_url) {
    return `${login_url}${endpoint}`;
  }
  // const host_url = "http://localhost:4083";
  return endpoint;
}

export function askUserLocation(userAllowLocation, userDeniedLocation, optionalObject = {}) {
  const defaultObject = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
    ...optionalObject
  };

  if (!navigator.geolocation) {
    console.log('Geolocation is not supported by your browser');
  } else {
    navigator.geolocation.getCurrentPosition(userAllowLocation, userDeniedLocation, defaultObject);
  }
}

export function getClientIP(req = {}) {
  const { headers = {}, connection = {} } = req;
  return headers['x-forwarded-for'] || connection.remoteAddress || '-';
}

export function getReferer(req = {}) {
  return req.originalUrl || '-';
}

export function getFetcherHeader(req = {}) {
  if (isNode()) {
    return {
      clientIP: getClientIP(req),
      referer: getReferer(req)
    };
  }
  return {};
}

function toMessage(val="") {
  if (val == null) return '';

  if (typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch {
      return 'Something went wrong.';
    }
  }

  return String(val);
}

